import { orderBy, sum, uniq } from 'lodash'
import I18n from 'core/i18n'
import {
  IBuildedTour,
  IWeeklyPost,
  IWeeklyTour,
  IBuildedTask,
  IFilterOptions,
  IWeeklyOtaReservation,
  IWeeklyTourGuide,
  IWeeklyGuide,
  ITask,
  IBuildedTourGroup,
  IConflictTour,
} from './weeklyTypes'
import { IGuideFilterOptions, IPaymentDetail } from 'static/interfaces'
import { SKILL_LEVELS, SKILL_CONDITION_TYPES } from 'static/constants'

type TourStatusClassName =
  | 'ok'
  | 'alert'
  | 'caution'
  | 'ng'
  | 'empty'
  | 'connected'
  | 'guideAndGuest'
  | ''
interface IBuildTasksResult {
  tasks: IBuildedTask[]
  allTaskDone: boolean
}

const getTaskClassByIndex = (index: number) => {
  switch (index) {
    case 0:
      return 'checkEmail'
    case 1:
      return 'checkHotel'
    case 2:
      return 'other'
    default:
      return ''
  }
}

// ツアーグループの表示名。番号があれば番号のみ、ない(採番機能リリース前の)グループは従来どおりIDで表示する
export const getTourGroupLabel = (tourGroup: { id: number; number?: number | null }) =>
  tourGroup.number ? String(tourGroup.number) : `ツアーグループ-${tourGroup.id}`

export const getGuideDisplayName = (guide?: IWeeklyGuide | null) =>
  guide?.fullname || guide?.username || '---'

export const buildTasks = (
  ota_reservations: IWeeklyOtaReservation[],
  defaultTasks,
  isPrivate
): IBuildTasksResult => {
  if (!isPrivate || ota_reservations.length === 0) {
    return { tasks: [], allTaskDone: false }
  }
  // プライベートツアーなので最初に1件のみを採用
  const otaReservation = ota_reservations[0]
  // 完了済のタスクを取得、整理
  const otaReservationTasks = otaReservation.ota_reservation_tasks
  const doneTaskIds = otaReservationTasks?.map(({ task_id }) => task_id) || []

  // 全件取得しているタスクと統合、完了済か否かを整理
  const convertedTasks: IBuildedTask[] = defaultTasks.map((task, i) => {
    const done = doneTaskIds.includes(task.id)
    const otaReservationTask = otaReservationTasks?.find(t => t.task_id === task.id)
    const otaReservationId = otaReservationTask?.ota_reservation_id || otaReservation?.id

    const buildedTask: IBuildedTask = {
      ...task,
      task_id: task.id,
      className: getTaskClassByIndex(i),
      ota_reservation_id: otaReservationId ?? null,
      ota_reservation_task_id: otaReservationTask?.id || null,
      done,
    }

    return buildedTask
  })

  return {
    tasks: convertedTasks,
    allTaskDone: convertedTasks.every(task => task.done),
  }
}

const getTourGroupReservedNumbers = (ota_reservations: IWeeklyOtaReservation[]) => {
  const initialReservedNumbers = {
    reservedNumber: 0,
    adultsNumber: 0,
    childrenNumber: 0,
    infantsNumber: 0,
  }

  return ota_reservations.reduce((p, ota_reservation) => {
    const { num_of_people, num_of_adults, num_of_children, num_of_infants } = ota_reservation

    return {
      reservedNumber: p.reservedNumber + num_of_people - num_of_infants,
      adultsNumber: p.adultsNumber + num_of_adults,
      childrenNumber: p.childrenNumber + num_of_children,
      infantsNumber: p.infantsNumber + num_of_infants,
    }
  }, initialReservedNumbers)
}

const buildTourGroups = (
  post: IWeeklyPost,
  tour: IWeeklyTour,
  ongoingOtaReservations: IWeeklyOtaReservation[],
  unAssignedTourGuides: IWeeklyTourGuide[],
  isPrivate: boolean,
  defaultTasks: ITask[]
) => {
  let unAssignedCount = 0
  return orderBy(tour.tour_groups, ['id'], ['asc']).map(tourGroup => {
    const otaReservations = ongoingOtaReservations.filter(
      otaReservation => otaReservation.jwt_tour_group_id === tourGroup.id
    )

    const connections = isPrivate
      ? otaReservations.map(otaReservation => otaReservation.connection ?? null)
      : []
    const connected =
      connections.length > 0 && connections.every(connection => connection?.status === 'connected')

    const buildedTasks: IBuildTasksResult = buildTasks(otaReservations, defaultTasks, isPrivate)

    const tourGroupReservedNumbers = getTourGroupReservedNumbers(otaReservations)

    const assignedTourGuide = tour.tour_guides.find(tg => tg.tour_group_id === tourGroup.id) || null
    const assignedGuide = assignedTourGuide?.guide || null
    if (!assignedGuide) {
      unAssignedCount += 1
    }

    const tourGroupStatus = assignedGuide
      ? 'ok'
      : unAssignedTourGuides.length !== 0 && unAssignedTourGuides.length >= unAssignedCount
      ? 'alert'
      : 'ng'
    const tourGroupMaxNumber = post.tour_group_max_attendees

    const paymentDetail: IPaymentDetail | null = assignedTourGuide?.payment_detail || null
    return {
      ...tourGroup,
      ...buildedTasks,
      ...tourGroupReservedNumbers,
      assignedOtaReservations: otaReservations,
      connected,
      assignedGuide,
      tourGroupStatus,
      maxNumber: tourGroupMaxNumber,
      paymentDetail,
      guideArrivedAt: assignedTourGuide?.arrived_at || null,
    }
  })
}

export const buildTours = (
  tours: Array<IWeeklyTour | IBuildedTour>,
  posts: IWeeklyPost[],
  tasks: ITask[],
  tourMemoTemplates: any[]
): IBuildedTour[] => {
  return tours.map(tour => {
    const ota_reservations = orderBy(tour.ota_reservations || [], ['created_at'], ['desc'])
    const post = posts.find(p => p.id === tour.post_id)
    const isPrivate = post.post_type === 'private'
    const ongoingOtaReservations =
      ota_reservations.filter(ota_reservation =>
        ['pending', 'confirmed'].includes(ota_reservation.booking_status_id)
      ) || []
    const canceledOtaReservations =
      ota_reservations.filter(ota_reservation =>
        ['canceled', 'declined'].includes(ota_reservation.booking_status_id)
      ) || []
    const unAssignedTourGuides = tour.tour_guides.filter(
      tg =>
        !tg.tour_group_id &&
        !['guide_declined', 'agent_canceled', 'agent_pre_canceled', 'agent_rejected'].includes(
          tg.request_status
        )
    )
    const canceledTourGuides = tour.tour_guides.filter(tg => {
      return ['agent_canceled', 'guide_declined', 'agent_pre_canceled', 'agent_rejected'].includes(
        tg.request_status
      )
    })
    const unassignedOtaReservations = ongoingOtaReservations.filter(
      ota_reservation => !ota_reservation.jwt_tour_group_id
    )
    const reservedNumber = sum(
      ongoingOtaReservations.map(
        ota_reservation => ota_reservation.num_of_people - ota_reservation.num_of_infants
      )
    )
    const showAlert =
      post.expandable &&
      (post.post_type === 'group' ? reservedNumber : ongoingOtaReservations.length) >=
        post.alert_number

    const tourGroups = buildTourGroups(
      post,
      tour,
      ongoingOtaReservations,
      unAssignedTourGuides,
      isPrivate,
      tasks
    )

    return {
      ...tour,
      job_summaries: tour.job_summaries ?? [],
      ota_reservations,
      post,
      maxNumber: post.tour_group_max_attendees || '-',
      unAssignedTourGuides,
      canceledTourGuides,
      reservedNumber,
      showAlert,
      tour_groups: orderBy(tourGroups, 'assignedGuide'),
      unassignedOtaReservations,
      canceledOtaReservations,
      ongoingOtaReservations,
      tourMemoTemplates,
    }
  })
}

export const buildFilterLabels = (filterOptions: IFilterOptions, posts) => {
  const labels = filterOptions.post_ids.map(post_id => {
    const post = posts.find(p => p.id === post_id)
    // DMCツアーは所要時間ごとに同名のPostがあるため、カレンダーのタイトルと同様に所要時間を付ける
    const duration = post?.dmc_tour_id ? ` (${post.duration}h)` : ''
    return { type: 'tour', label: `${post?.short_name || post?.name}${duration}` }
  })
  filterOptions.post_types?.map(post_type => {
    labels.push({ type: 'tour', label: I18n.t(`post.${post_type}_tour`) })
  })
  filterOptions.tour_group_status.map(status => {
    labels.push({ type: 'status', label: I18n.t(`tours.weekly.tour_group_status.${status}`) })
  })
  Object.keys(filterOptions)
    .filter(key => !['post_ids', 'post_types', 'tour_group_status'].includes(key))
    .map(key => {
      if (filterOptions[key] !== undefined) {
        labels.push({ type: 'status', label: I18n.t(`tours.weekly.${key}`) })
      }
    })
  return labels
}

const getGenderLabelKeyFromValue = (value: string) => {
  switch (value) {
    case '0':
      return 'female'
    case '1':
      return 'male'
    case '2':
      return 'other'
    default:
      return ''
  }
}

export const buildGuideFilterLabels = (
  filterOptions: IGuideFilterOptions,
  agentSkills,
  agentTourSkills,
  agentRanks
) => {
  const labels = []
  if (filterOptions.name !== '') {
    labels.push({ type: 'name', label: filterOptions.name })
  }
  if (filterOptions.email !== '') {
    labels.push({ type: 'email', label: filterOptions.email })
  }
  if (filterOptions.tel !== '') {
    labels.push({ type: 'tel', label: filterOptions.tel })
  }
  filterOptions.ages.forEach(age => {
    labels.push({ type: 'ages', label: `${age}代${age === 50 ? '以上' : ''}` })
  })

  // genderの値は数値であるため簡易的措置
  const genderLabelKey = getGenderLabelKeyFromValue(filterOptions.gender)
  if (genderLabelKey !== '') {
    labels.push({
      type: 'gender',
      label: I18n.t(`enums.user.gender.${genderLabelKey}`, { locale: 'ja' }),
    })
  }

  if (filterOptions.tour_guide_count_min !== null || filterOptions.tour_guide_count_max !== null) {
    const min = filterOptions.tour_guide_count_min ?? 0
    const max = filterOptions.tour_guide_count_max ?? ''
    labels.push({ type: 'tour_guide_count', label: `${min} ~ ${max}` })
  }

  filterOptions.agent_skills?.map(id => {
    labels.push({ type: 'agent_skills', label: agentSkills.find(as => as.id === id)?.name })
  })

  filterOptions.assign_guide_tour_skills?.forEach(agts => {
    const tourSkill = agentTourSkills.find(ats => ats.id === Number(agts.tour_skill_id))
    if (tourSkill) {
      const name = tourSkill.name
      const level = agts.level ? SKILL_LEVELS[Number(agts.level)] : ''
      const condition = SKILL_CONDITION_TYPES[agts.condition_type || 'exact']
      labels.push({
        type: 'assign_guide_tour_skills',
        label: `${name} ${level ? ` ${level}${condition}` : ''}`.trim(),
      })
    }
  })

  filterOptions.agent_ranks.forEach(id => {
    labels.push({ type: 'agent_ranks', label: agentRanks.find(ar => ar.id === id)?.name })
  })

  filterOptions.statuses.forEach(status => {
    labels.push({
      type: 'statuses',
      label: I18n.t(`enums.user.status.${status}`, { locale: 'ja' }),
    })
  })

  if (filterOptions.login_status === 'never_logged_in') {
    labels.push({ type: 'login_status', label: '未ログインのみ' })
  } else if (filterOptions.login_status === 'logged_in') {
    labels.push({ type: 'login_status', label: 'ログイン済みのみ' })
  }

  return labels
}

export const clearUrlFilterOptions = filterOptions => {
  const url = new URL(window.location.href)
  const currentUrlParams = url.searchParams
  Object.keys(filterOptions).map(key => {
    currentUrlParams.delete(key)
    currentUrlParams.delete(`${key}[]`)
  })
  url.search = currentUrlParams.toString()
  const newUrl = url.toString()
  window.history.replaceState(
    { ...window.history.state, as: newUrl, url: newUrl },
    undefined,
    newUrl
  )
}

export const filteredTours = (tours: IBuildedTour[], filterOptions: IFilterOptions) => {
  let result = tours
  // ツアー種類とツアータイプはAND条件、高度なフィルタはOR条件で適用する。
  if (filterOptions.post_ids?.length > 0) {
    result = result.filter(tour => filterOptions.post_ids.includes(tour.post_id))
  }

  if (filterOptions.post_types?.length > 0) {
    result = result.filter(tour => filterOptions.post_types.includes(tour.post.post_type))
  }

  if (
    filterOptions.tour_group_status?.length > 0 ||
    filterOptions.alert !== undefined ||
    filterOptions.insufficient_with_guide !== undefined ||
    filterOptions.insufficient_with_no_guide !== undefined ||
    filterOptions.connected !== undefined ||
    filterOptions.not_connected !== undefined ||
    filterOptions.all_task_done !== undefined ||
    filterOptions.has_task !== undefined
  ) {
    let statusFilteredTours = []
    let alertFilteredTours = []
    let insufficientWithGuideFilteredTours = []
    let insufficientWithNoGuideFilteredTours = []

    if (filterOptions.tour_group_status?.length > 0) {
      statusFilteredTours = result.filter(tour =>
        tour.tour_groups.find(tour_group =>
          filterOptions.tour_group_status.includes(tour_group.tourGroupStatus)
        )
      )
    }

    if (filterOptions.alert !== undefined) {
      alertFilteredTours = result.filter(tour => tour.showAlert === filterOptions.alert)
    }
    if (filterOptions.insufficient_with_guide !== undefined) {
      insufficientWithGuideFilteredTours = result.filter(tour => {
        // bokun連携かつgroupツアーは催行最小人数を1にする
        const min_people =
          !!tour.post.bokun_activity_id && tour.post.post_type === 'group'
            ? 1
            : tour.post.min_people
        // ガイドが設定されているツアーグループのうち、ひとつでも催行人数未達があるかチェック
        const hasInsufficientTourGroup = tour.tour_groups.some(
          tourGroup => !!tourGroup.assignedGuide && min_people > tourGroup.reservedNumber
        )
        return hasInsufficientTourGroup === filterOptions.insufficient_with_guide
      })
    }
    if (filterOptions.insufficient_with_no_guide !== undefined) {
      insufficientWithNoGuideFilteredTours = result.filter(tour => {
        // bokun連携かつgroupツアーは催行最小人数を1にする
        const min_people =
          !!tour.post.bokun_activity_id && tour.post.post_type === 'group'
            ? 1
            : tour.post.min_people
        // ガイドが設定されていないツアーグループのうち、ひとつでも催行人数未達があるかチェック
        const hasInsufficientTourGroup = tour.tour_groups.some(
          tourGroup => !tourGroup.assignedGuide && min_people > tourGroup.reservedNumber
        )
        return hasInsufficientTourGroup === filterOptions.insufficient_with_no_guide
      })
    }

    const privateTours = result.filter(tour => tour.post.post_type === 'private')
    const filteredPrivateTours = privateTours.filter(tour => {
      let match = false
      if (filterOptions.connected) {
        match = tour.tour_groups.some(tg => tg.connected)
      }
      if (!match && filterOptions.not_connected) {
        match = tour.tour_groups.some(tg => !tg.connected)
      }
      if (!match && filterOptions.all_task_done) {
        match = tour.tour_groups.some(tg => tg.allTaskDone)
      }
      if (!match && filterOptions.has_task) {
        match = tour.tour_groups.some(tg => !tg.allTaskDone)
      }
      return match
    })

    result = orderBy(
      uniq(
        statusFilteredTours
          .concat(alertFilteredTours)
          .concat(insufficientWithGuideFilteredTours)
          .concat(insufficientWithNoGuideFilteredTours)
          .concat(filteredPrivateTours)
      ),
      ['day', 'start_time'],
      ['asc', 'asc']
    )
  }
  return result
}

export const updatedTourByTourGroup = (tour, tourGroupId, params) => {
  const prevTourGroups = tour.tour_groups
  const index = prevTourGroups.findIndex(tg => tg.id === tourGroupId)
  const newTourGroups =
    index === -1
      ? prevTourGroups
      : [
          ...prevTourGroups.slice(0, index),
          { ...prevTourGroups[index], ...params },
          ...prevTourGroups.slice(index + 1),
        ]
  return { ...tour, tour_groups: newTourGroups }
}

export const updatedTourByOtaReservation = (tour, otaReservationId, params) => {
  const prevOtaReservations = tour.ota_reservations
  const index = prevOtaReservations.findIndex(tr => tr.id === otaReservationId)
  const newOtaReservations =
    index === -1
      ? prevOtaReservations
      : [
          ...prevOtaReservations.slice(0, index),
          { ...prevOtaReservations[index], ...params },
          ...prevOtaReservations.slice(index + 1),
        ]
  return { ...tour, ota_reservations: newOtaReservations }
}

export const getTourGroupStyleClass = ({
  tourGroupStatus,
  assignedGuide,
  reservedNumber,
  connected = false,
  isPrivate = false,
}: {
  tourGroupStatus: 'ok' | 'alert' | 'ng'
  assignedGuide: any | null
  reservedNumber: number
  connected?: boolean
  allTaskDone?: boolean
  isPrivate?: boolean
}): TourStatusClassName => {
  if (tourGroupStatus === 'alert') {
    return 'alert'
  }

  const hasGuest = reservedNumber > 0
  const hasGuide = assignedGuide !== null

  if (isPrivate) {
    if (!hasGuide) {
      return hasGuest ? tourGroupStatus : 'empty'
    }

    if (!hasGuest) {
      // ガイドありゲストなし
      return 'caution'
    }

    if (connected) {
      return 'connected'
    }

    return 'guideAndGuest'
  }

  if (hasGuest) {
    return tourGroupStatus
  }

  if (hasGuide) {
    // ガイドありゲストなし
    return 'caution'
  }

  return 'empty'
}

// ツアーガイドのステータス別の配列を返す関数
export const getGroupUnassignedTourGuidesByStatus = (
  unAssignedTourGuides: IWeeklyTourGuide[]
): [IWeeklyTourGuide[], IWeeklyTourGuide[], IWeeklyTourGuide[]] => {
  const agent_official_requested = []
  const guide_approved = []
  const agent_pre_requested = []
  unAssignedTourGuides.forEach(guide => {
    switch (guide.request_status) {
      case 'agent_official_requested':
        agent_official_requested.push(guide)
        break
      case 'guide_approved':
        guide_approved.push(guide)
        break
      case 'agent_pre_requested':
        agent_pre_requested.push(guide)
        break
    }
  })
  return [agent_official_requested, guide_approved, agent_pre_requested]
}

export const getConnectionConfirmMessage = (tourGroup: IBuildedTourGroup) => {
  if (!tourGroup.assignedGuide) {
    return { connectMessage: '', reConnectMessage: '' }
  }

  const connectMessage = `${
    tourGroup.assignedGuide.username
  } と ${tourGroup.assignedOtaReservations
    .map(ota_reservation => ota_reservation.customer_full_name)
    .join(', ')} をつなぎますか？`
  const reConnectMessage =
    '既につないでいます。\n解除したい場合はゲスト欄・またはガイド欄からツアーグループ解除してください'
  return {
    connectMessage,
    reConnectMessage,
  }
}
/**
 * Weeklyにて、表示中のツアーの中から最新の競合ツアーを取得する
 * @param tourId
 * @param guideId
 * @param conflictingTourIds
 * @param allTours
 */
export const getLatestConflictTour = (
  tourId: number,
  guideId: number,
  conflictingTourIds: number[] | undefined,
  allTours: IBuildedTour[]
): IConflictTour | null => {
  if (!conflictingTourIds || conflictingTourIds.length === 0) {
    return null
  }

  const conflictingTourIdSet = new Set(conflictingTourIds)
  let latestConflictTour: IConflictTour | null = null

  for (const tour of allTours) {
    if (tour.id === tourId || !conflictingTourIdSet.has(tour.id)) {
      continue
    }

    const conflictingTourGuide = tour.tour_guides.find(
      candidateGuide => candidateGuide.guide_id === guideId
    )

    if (
      !conflictingTourGuide ||
      (conflictingTourGuide.request_status !== 'agent_pre_requested' &&
        conflictingTourGuide.request_status !== 'agent_official_requested' &&
        conflictingTourGuide.request_status !== 'guide_declined' &&
        conflictingTourGuide.request_status !== 'agent_pre_canceled')
    ) {
      continue
    }

    const isLaterTour =
      !latestConflictTour ||
      tour.day > latestConflictTour.tour.day ||
      (tour.day === latestConflictTour.tour.day &&
        tour.start_time > latestConflictTour.tour.start_time)

    if (isLaterTour) {
      latestConflictTour = {
        tour,
        requestStatus: conflictingTourGuide.request_status,
      }
    }
  }

  return latestConflictTour
}

/**
 * Weeklyにて、Drawer中央の「予約一覧」パネルの並び順に整列した予約配列を返す
 * - 第1キー: num_of_people（インファントを含む合計人数）。昇順/降順トグルの対象
 * - 第2キー: created_at。トグルには追従せず常に降順（左パネル・buildToursの並び順とは別物）
 * @param otaReservations 並び替え対象の予約
 * @param orderDesc 第1キーを降順にするか
 */
export const sortedOtaReservations = (
  otaReservations: IWeeklyOtaReservation[],
  orderDesc: boolean
): IWeeklyOtaReservation[] =>
  orderBy(otaReservations, ['num_of_people', 'created_at'], [orderDesc ? 'desc' : 'asc', 'desc'])
